import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import { useAppContext } from '../../../redux/slices/appContext'
import ClientModalAccounts from '../ClientDashboard/components/ClientModalAccounts'
import { useAccountSearchMultiple } from '../../../api/coreData'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'

const DEFAULT_FILTERS = {}

const useTitle = (label) => {
  return useMemo(() => {
    if (Array.isArray(label)) {
      if (label.length > 1) {
        return label
      } else if (label.length === 1) {
        return [label[0], label[0]]
      }
      return [null, null]
    }
    return [label, label]
  }, [label])
}

const useClientAccounts = ({
  filters
}) => {
  const { clientId } = useAppContext()

  const query = useMemo(() => ({
    assignedAccounts: {
      resultType: 'total',
      filters: {
        assignedToClientIds: [clientId],
        ...(filters ?? {})
      }
    },
    hasExceptions: {
      resultType: 'total',
      filters: {
        assignedToClientIds: [clientId],
        hasException: true,
        ...(filters ?? {})
      }
    },
    isAutomatedNotUpdating: {
      resultType: 'total',
      filters: {
        assignedToClientIds: [clientId],
        isAutomatedNotUpdating: true,
        ...(filters ?? {})
      }
    }
  }), [clientId, filters])
  const { data, error, isLoading } = useAccountSearchMultiple(query)
  const [accountCount, showWarning] = useMemo(() => {
    if (error || isLoading) return [null, null]
    const total = data?.assignedAccounts?.total
    const totalHasExceptions = data?.hasExceptions?.total || 0
    const totalIsAutomatedNotUpdating = data?.isAutomatedNotUpdating?.total || 0
    return [total, totalHasExceptions > 0 || totalIsAutomatedNotUpdating > 0]
  }, [data, error, isLoading])

  return { accountCount, showWarning }
}

function ClientAccountInfo ({
  filters,
  subTitle,
  title,
  label,
  columns,
  format,
  defaultSort,
  className,
  style
}) {
  const { formatter } = useFormattingContext()
  const [showModal, setShowModal] = useState(false)
  const { accountCount } = useClientAccounts({
    filters
  })
  const [singularTitle, pluralizedTitle] = useTitle(title)
  const [singleLabel, multiLabel] = useTitle(label)

  return (
    <div>
      <Tooltip title='Accounts at a glance'>
        <div role='button' onClick={() => setShowModal(true)} className={clsx(className, 'flex flex-col items-center')} style={style}>
          <div className='font-size-sm'>
            {formatter(accountCount, format)}
          </div>
          {(singleLabel || multiLabel) ? (
            <div className='font-size-xs font-weight-thin' style={{ fontWeight: 'var(--font-weight-thin)' }}>
              {accountCount === 1 ? singleLabel : multiLabel}
            </div>
          ) : null}
        </div>
      </Tooltip>
      <ClientModalAccounts
        open={showModal}
        modalTitle={accountCount === 1 ? singularTitle : pluralizedTitle}
        modalSubtitle={subTitle}
        filters={filters}
        columns={columns}
        defaultSort={defaultSort}
        handleClose={() => setShowModal(false)}
      />
    </div>
  )
}

ClientAccountInfo.propTypes = {
  filters: PropTypes.object,
  title: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string
  ]),
  label: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string
  ]),
  subTitle: PropTypes.string,
  format: PropTypes.string,
  columns: PropTypes.array,
  defaultSort: PropTypes.array,
  className: PropTypes.string,
  style: PropTypes.object
}

ClientAccountInfo.defaultProps = {
  filters: DEFAULT_FILTERS,
  label: ['Account', 'Accounts'],
  title: ['Account', 'Accounts'],
  format: '0'
}

export default ClientAccountInfo
